'use client';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { Typography, theme, IcChevronDown, Flex, SicInfoCircle } from '@bgzt/ui';
import clsx from 'clsx';

import { reviewStatisticsOptions } from '../../_queries';
import { useReviewGuideBottomSheet } from '../../_hooks';
import { Section, Spacing } from '~/components';
import { ShopReviewsStarRating } from '../ShopReviewsStarRating';

import styles from './ShopReviewsSummary.module.css';

type Props = {
  shopUid: number;
};
export const ShopReviewsSummary = ({ shopUid }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: reviewStatisticsData } = useQuery(reviewStatisticsOptions(shopUid));
  const isRenderKeywordMore = reviewStatisticsData && reviewStatisticsData.reviewKeywordStatistics?.length > 3;
  const renderReviewCount = (count?: number) => {
    if (!count) return '';
    if (count > 9999) {
      return '9,999+';
    }
    return count;
  };

  const handleInfoClick = useReviewGuideBottomSheet(reviewStatisticsData?.guide);

  return (
    <Section className={styles.container}>
      <Spacing height={30} />
      <Flex gap={4} align="center">
        <Typography variant="L3" color="gray-900">
          이 상점의 거래후기 {renderReviewCount(reviewStatisticsData?.reviewCount)}
        </Typography>
        <button className={styles.infoButton} onClick={handleInfoClick}>
          <SicInfoCircle size={16} color={theme.colors['gray-600']} />
        </button>
      </Flex>

      <Spacing height={16} />
      <div className={styles.summary}>
        <div className={clsx(styles.summaryItem, styles.averageGrade)}>
          <Typography variant="L2" color="gray-900">
            {reviewStatisticsData?.averageGrade}
          </Typography>
          <div className={styles.starContainer}>
            <ShopReviewsStarRating grade={reviewStatisticsData?.averageGrade ?? 0} />
          </div>
        </div>
        <div className={clsx(styles.summaryItem, styles.goodReviewPercentage)}>
          <Typography variant="L2" color="gray-900">
            {reviewStatisticsData?.goodReviewPercentage}%
          </Typography>
          <Typography variant="L12" color="gray-500">
            만족후기
          </Typography>
        </div>
      </div>
      <Spacing height={16} />
      <div
        className={clsx(
          styles.keywordContainer,
          isRenderKeywordMore && styles.keywordContainerMore,
          isOpen && styles.open,
        )}
      >
        <ul className={styles.keywordList}>
          {reviewStatisticsData?.reviewKeywordStatistics.map((review) => (
            <li key={review.title} className={styles.keywordItem}>
              <div className={styles.keywordTitle}>
                <Image src={review.iconUrl} alt={review.title} width={14} height={14} unoptimized />
                <Typography variant="L8" color="gray-800">
                  {review.title}
                </Typography>
              </div>
              <div className={styles.reviewCount}>
                <Typography variant="L7" color="gray-500">
                  {review.count.toLocaleString()}
                </Typography>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {isRenderKeywordMore && (
        <div className={clsx(styles.moreButton, isOpen && styles.open)} onClick={() => setIsOpen((prev) => !prev)}>
          <IcChevronDown
            size={14}
            fill={theme.colors['gray-900']}
            className={clsx(styles.moreIcon, isOpen && styles.open)}
          />
        </div>
      )}

      <Spacing height={48} />
    </Section>
  );
};
